/** Juftlik qadami (demo `matchStep`): chap — so'zlar, o'ng — tarjimalar; taymer, xatolar, rekord (`S.best`). */
import { useEffect, useRef, useState } from 'react';
import { fmtTime, shuffle } from '../../../data';
import type { Word } from '../../../data/types';
import { t, tn } from '../../../i18n';
import { chime } from '../../../lib/sound';
import { speak } from '../../../lib/speech';
import { haptic } from '../../../lib/telegram';
import { S } from '../../../store';
import { Ic } from '../../../ui/Icon';
import { addXp, next, setMatch, type Step } from '../engine';
import { Frame, Prompt } from './common';

type Side = 'l' | 'r';
const key = (side: Side, id: number) => `${side}:${id}`;

export function MatchStep({ s }: { s: Step }) {
  const ws = s.ws ?? [];
  const [A] = useState(() => shuffle(ws));
  const [B] = useState(() => shuffle(ws));
  const [sel, setSel] = useState<{ id: number; side: Side } | null>(null);
  const [done, setDone] = useState<number[]>([]);
  const [bad, setBad] = useState<string[]>([]);       // 450ms qizil juft
  const [miss, setMiss] = useState(0);
  const [sec, setSec] = useState(0);
  const [end, setEnd] = useState<{ xp: number; extra: string } | null>(null);
  const t0 = useRef(Date.now());
  const badTm = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!s.timed || end) return;
    const tm = setInterval(() => setSec(Math.floor((Date.now() - t0.current) / 1000)), 500);
    return () => clearInterval(tm);
  }, [s.timed, end]);
  useEffect(() => () => clearTimeout(badTm.current), []);

  const click = (w: Word, side: Side) => {
    if (end || done.includes(w.id)) return;
    if (!sel || sel.side === side) { setSel({ id: w.id, side }); if (side === 'l') speak(w.w); return; }
    if (sel.id === w.id) {
      const nd = [...done, w.id]; setDone(nd); setSel(null);
      if (nd.length === ws.length) {
        const secs = Math.max(1, Math.round((Date.now() - t0.current) / 1000)), xp = Math.max(5, 30 - miss * 5);
        addXp(xp); setMatch({ secs, mistakes: miss, timed: !!s.timed });
        let extra = '';
        if (s.timed) { const st = S(); const rec = !st.best || secs < st.best; if (rec) st.set({ best: secs }); extra = ` · ${fmtTime(secs)}${rec ? t(' · yangi rekord!') : ''}`; }
        chime(); setEnd({ xp, extra });
      }
    } else {
      setMiss(miss + 1); haptic.bad();
      const pair = [key(sel.side, sel.id), key(side, w.id)]; setBad(pair); setSel(null);
      clearTimeout(badTm.current); badTm.current = setTimeout(() => setBad([]), 450);
    }
  };
  const cls = (w: Word, side: Side) => {
    const k = key(side, w.id);
    return ['m', sel && sel.side === side && sel.id === w.id ? 'sel' : '', done.includes(w.id) ? 'done' : '', bad.includes(k) ? 'bad' : '', bad.includes(k) && bad[0] === k ? 'sel' : ''].filter(Boolean).join(' ');
  };

  const sheet = end ? (
    <div className="sheet ok">
      <div className="hd"><i><Ic n="check" style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i><b>{t('Hammasi topildi!')}</b><span className="xp">+{end.xp} XP</span></div>
      <p className="msg" style={{ color: 'var(--success-ink)' }}>{miss ? tn('# ta xato', miss) : t('Xatosiz')}{end.extra}</p>
      <button className="btn" id="cont" autoFocus onClick={next}>{t('Davom etish')}</button>
    </div>
  ) : null;

  return (
    <Frame sheet={sheet}>
      <div className="between">
        <Prompt text="Juftini toping" />
        {s.timed && <b className="t15b" id="clk" style={{ color: 'var(--info-ink)', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(sec)}</b>}
      </div>
      <div className="match">
        <div>{A.map((w) => <button key={w.id} className={cls(w, 'l')} data-id={w.id} data-s="l" onClick={() => click(w, 'l')}>{w.w}</button>)}</div>
        <div>{B.map((w) => <button key={w.id} className={cls(w, 'r')} data-id={w.id} data-s="r" onClick={() => click(w, 'r')}>{w.uz}</button>)}</div>
      </div>
    </Frame>
  );
}
